import { AnimatedSprite, Assets, Container, Graphics, Sprite, Texture } from 'pixi.js';
import { GameObject } from './GameObject';

import type { AvatarViewEntity } from '$lib/view';
import gsap from 'gsap';
import { calculateExplosionTiles } from './BombObject';

export class AvatarObject extends GameObject {
	protected playerControlled: boolean = false;
	protected epochAnim:
		| {
				timeline: gsap.core.Timeline;
				epoch: number;
		  }
		| undefined;
	// Track bomb preview objects for smooth transitions
	protected bombPreviews: Map<string, Container> = new Map();
	protected sprite: Sprite;
	protected greenSquare: Graphics;
	protected redSquare: Graphics;
	protected deadCross: Graphics;
	protected enteringSprite: Sprite;

	constructor(
		protected world: {
			pathDisplayObject: Container;
			getBombObject?: (id: string) => any; // Function to get existing bomb objects
		},
		protected entity: AvatarViewEntity
	) {
		super();
		const textures = [
			Assets.get('sprites').textures['Cat_idle_bottom_1.png'],
			Assets.get('sprites').textures['Cat_idle_bottom_2.png'],
			Assets.get('sprites').textures['Cat_idle_bottom_3.png'],
			Assets.get('sprites').textures['Cat_idle_bottom_4.png'],
			Assets.get('sprites').textures['Cat_idle_bottom_5.png'],
			Assets.get('sprites').textures['Cat_idle_bottom_6.png']
		];
		this.sprite = new AnimatedSprite({
			textures,
			animationSpeed: 0.15,
			loop: true,
			autoPlay: true
		});
		this.sprite.zIndex = 1;
		this.addChild(this.sprite);
		this.sprite.scale = 10 / 28;
		this.sprite.anchor.set(0.5, 0.7);

		{
			const textures: Texture[] = [];
			for (let i = 1; i <= 21; i++) {
				if (i < 10) {
					textures.push(Assets.get('sprites').textures[`entry_00${i}.png`]);
				} else {
					textures.push(Assets.get('sprites').textures[`entry_0${i}.png`]);
				}
			}

			this.enteringSprite = new AnimatedSprite({
				textures,
				animationSpeed: 0.15,
				loop: true,
				autoPlay: true
			});
			this.enteringSprite.zIndex = 0;
			this.addChild(this.enteringSprite);
			this.enteringSprite.scale = 10 / 64;
			this.enteringSprite.anchor.set(0.5, 0.7);
			this.enteringSprite.visible = false;
		}

		this.greenSquare = new Graphics().rect(-5, -5, 10, 10).stroke({ width: 1, color: 0x00ff00 });
		this.addChild(this.greenSquare);
		this.greenSquare.visible = false;

		this.redSquare = new Graphics().rect(-5, -5, 10, 10).stroke({ width: 1, color: 0xff0000 });
		this.addChild(this.redSquare);
		this.redSquare.visible = false;

		this.deadCross = new Graphics()
			.moveTo(-5, -5)
			.lineTo(5, 5)
			.moveTo(-5, 5)
			.lineTo(5, -5)
			.stroke({ width: 1, color: 0xff0000 });
		this.addChild(this.deadCross);
		this.deadCross.zIndex = 2;
		this.deadCross.visible = false;
	}

	update(entity: AvatarViewEntity, epoch: number) {
		this.entity = entity;

		if (entity.life == 0) {
			this.deadCross.visible = true;
		} else {
			this.deadCross.visible = false;
		}

		this.greenSquare.visible = false;
		this.redSquare.visible = false;
		// this.deadCross.visible = true;

		if (this.playerControlled) {
			// this.zIndex = 2;
			// TODO move that elsewhere and remove the need to delete all and reconstruct
			// Make sure to destroy all children first to prevent memory leaks
			this.world.pathDisplayObject.removeChildren();
			// Clear bomb previews from previous update
			this.bombPreviews.clear();

			const actions = entity.plannedActions;
			if (actions) {
				for (const action of actions) {
					if (action.type === 'placeBomb' || action.type === 'placeBombDelayed') {
						// Draw bomb placement indicator
						const bombPreview = new Container();
						const explosionGraphics = new Graphics();
						explosionGraphics.alpha = 0.7;
						explosionGraphics.x = 10 * action.x;
						explosionGraphics.y = 10 * action.y;
						const bombGraphics = new Graphics();
						bombPreview.addChild(bombGraphics);
						bombPreview.addChild(explosionGraphics);
						const isDelayed = action.type === 'placeBombDelayed';
						const color = isDelayed ? 0xff8800 : 0xff0000; // Orange for delayed, red for immediate

						// Draw bomb body with a subtle outline
						bombGraphics.circle(0, 0, 3).fill({ color });
						bombGraphics.circle(0, 0, 3).stroke({ width: 1, color: 0xffffff, alpha: 0.5 });
						// Add a small dot in the center for detail
						bombGraphics.circle(0, 0, 1).fill({ color: 0xffffff });

						bombGraphics.x = 10 * action.x;
						bombGraphics.y = 10 * action.y;
						bombGraphics.alpha = 0.8; // Make it slightly transparent

						const tiles = calculateExplosionTiles(action, 3);
						for (const tileKey of tiles) {
							const [x, y] = tileKey.split(',').map(Number);
							// Calculate pixel position relative to bomb center
							const pixelX = (x - action.x) * 10;
							const pixelY = (y - action.y) * 10;

							// Draw explosion circle (orange/yellow)
							explosionGraphics.circle(pixelX, pixelY, 4).fill({ color });
						}

						this.world.pathDisplayObject.addChild(bombPreview);

						// Add a subtle pulsing animation to make bomb previews more noticeable
						gsap.to(bombGraphics.scale, {
							x: 1.2,
							y: 1.2,
							duration: 0.5,
							repeat: -1,
							yoyo: true,
							delay: Math.random() / 5,
							ease: 'power2.inOut'
						});

						// Store the bomb preview for potential transition
						const bombKey = `${action.x},${action.y}`;
						this.bombPreviews.set(bombKey, bombPreview);
					} else if (action.type === 'move' || action.type === 'enter') {
						// Draw path movement (existing code)
						const graphics = new Graphics();
						this.world.pathDisplayObject
							.addChild(graphics)
							.rect(-5 + 4, -5 + 4, 2, 2)
							.fill(0x00ff00);
						graphics.x = 10 * action.x;
						graphics.y = 10 * action.y;
					}
				}
			}

			// Handle transition from preview bombs to real bomb objects
			this.handleBombTransitions();

			// if (entity.epoch > time.now() - 0.9) {
			// 	this.greenSquare.visible = false;
			// 	this.redSquare.visible = true;
			// } else {
			this.greenSquare.visible = true;
			this.redSquare.visible = false;
			// }
		}
		const destination = {
			x: 10 * entity.position.x,
			y: 10 * entity.position.y
		};

		// console.log(`destination`, { x: entity.position.x, y: entity.position.y });

		const moveActions = entity.previousActions.filter((v) => v.type === 'move');
		if (
			moveActions.length > 0 &&
			!(this.position.x == destination.x && this.position.y == destination.y) &&
			(!this.epochAnim || this.epochAnim.epoch !== epoch)
		) {
			// TODO ignore non-move actions ? or deal with them differently
			const duration = moveActions.length > 20 ? 2.2 / moveActions.length : 0.2;
			const tl = gsap.timeline();
			for (const p of moveActions) {
				tl.to(this.position, { x: 10 * p.x, y: 10 * p.y, duration });
				// console.log(`step`, { x: p.x, y: p.y });
			}
			this.epochAnim = {
				epoch,
				timeline: tl
			};
		} else {
			this.position.x = destination.x;
			this.position.y = destination.y;
		}

		if (entity.entering) {
			this.enteringSprite.visible = true;
			this.sprite.visible = false;
			this.greenSquare.visible = false;
		} else {
			this.enteringSprite.visible = false;
			this.sprite.visible = true;
		}
	}

	// Handle smooth transitions from bomb previews to real bomb objects
	private handleBombTransitions() {
		if (!this.world.getBombObject) return;

		for (const [bombKey, previewGraphics] of this.bombPreviews) {
			const [x, y] = bombKey.split(',').map(Number);

			// Check if a real bomb object exists at this position
			// This is a simplified check - in a real implementation, you'd want to track bomb IDs properly
			const realBombExists = this.checkForRealBomb(x, y);

			if (realBombExists) {
				// Smooth fade out the preview and create a transition effect
				gsap.to(previewGraphics, {
					alpha: 0,
					scaleX: 1.5,
					scaleY: 1.5,
					duration: 0.3,
					ease: 'power2.out',
					onComplete: () => {
						this.world.pathDisplayObject.removeChild(previewGraphics);
						this.bombPreviews.delete(bombKey);
					}
				});
			}
		}
	}

	// Check if a real bomb object exists at the given position
	private checkForRealBomb(x: number, y: number): boolean {
		if (!this.world.getBombObject) return false;

		// Check all game objects for bombs at this position
		for (let i = 0; i < 10; i++) {
			// Check potential bomb IDs (this could be improved)
			const bombId = `bomb_${x}_${y}_${i}`;
			const bombObject = this.world.getBombObject(bombId);

			if (bombObject && bombObject instanceof GameObject) {
				// Verify this is actually a bomb at the correct position
				const expectedX = 10 * x;
				const expectedY = 10 * y;
				if (bombObject.x === expectedX && bombObject.y === expectedY) {
					return true;
				}
			}
		}

		// In a more complete implementation, we would:
		// 1. Access the actual game state/store
		// 2. Check for bomb entities at this position
		// 3. Return true when a real bomb should replace the preview

		return false;
	}

	markAsPlayerControlled(isPlayerControlled: boolean) {
		if (!isPlayerControlled && this.playerControlled) {
			this.world.pathDisplayObject.removeChildren();
		}
		this.playerControlled = isPlayerControlled;
	}

	onRemoved() {
		if (this.playerControlled) {
			this.world.pathDisplayObject.removeChildren();
		}
	}
}
